import Combobox from '@/components/custom/Combobox';
import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
// import { AddCrmProjectModal } from '@/features/crm/components/organisms/AddCrmModal';
import { useProjectStore } from "@/lib/zustand/useStore";
import { zodResolver } from '@hookform/resolvers/zod';
import useEditProject from '@query/project/useEditProject';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from "react-hook-form";

import { BorderBox } from '@/components/atoms/BorderBox';
import { DynamicHeightScrollArea } from '@/components/atoms/DynamicHeightScrollArea';
import { ParameterSelect } from '@/components/atoms/ParameterSelect';
import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useGetOrganisationCrmList } from '@/features/crm/hooks/useQuery/useGetOrganisationCrmList';
import { useGetCrmProjectsByCrmId } from '@/features/crm_project/hooks/useQuery/useGetCrmProjectsByCrmId';
import { SelectCrmSystemSanitizedCombobox } from '@/features/crm_system/components/molecules/SelectCrmSystemSanitizedCombobox';
import { useGetProjectById } from '@/lib/useQuery/project/useGetProjectById';
import { matchConfigurationDataGrouped } from '@/lib/utils/crm/matchConfigurationData';
import { Loader, RotateCcw } from 'lucide-react';
import { Link, useParams } from "react-router-dom";
import { editProjectSchema, EditProjectSchemaType } from '.';
import { useUpdateCrmProject } from '@/features/crm_project/hooks/useMutation/useUpdateCrmProject';
import { addCrmModalSimplified } from "@/features/crm/components/organisms/AddCrmModalSimplified"
import { useGetCrmProjectById } from '@/features/crm_project/hooks/useQuery/useGetCrmProjectById';

export function EditProject() {
    const { orgId } = useParams();
    const { project } = useProjectStore();

    const { data: projectData, isPending: isPendingGetProjectData } = useGetProjectById({ projectId: project?.id });



    const form = useForm<EditProjectSchemaType>({ resolver: zodResolver(editProjectSchema) });
    const formCrmProjectId = form.watch("crm_project_id", null);

    console.log(formCrmProjectId)

    const { data: crmProjectData, isPending: isPendingCrmProjectData } = useGetCrmProjectById({ crmProjectId: formCrmProjectId });

    const selectedCRMSystem = form.watch("crm_system", '');
    const selectedCRM = form.watch("crm_id", '');
    const orgCrmBoxValue = form.watch("crm_project_id", null);
    const fullConfig = useMemo(() => {
        if (!crmProjectData?.crm) return undefined;
        return matchConfigurationDataGrouped(crmProjectData.crm, crmProjectData)
    }, [crmProjectData])
    const [parameters, setParameters] = useState(fullConfig?.withDefaultConfiguration?.parameter?.project || [])
    const { data: crmsData } = useGetOrganisationCrmList(Number(orgId));
    const crmsDataForSelectedSystem = useMemo(() => {
        if (!selectedCRMSystem || !crmsData) return [];
        return crmsData.filter((crm) => String(crm.crm_system) === String(selectedCRMSystem));
    }, [selectedCRMSystem, crmsData]);
    const { data: crmProjectsData } = useGetCrmProjectsByCrmId(Number(selectedCRM));

    const [isOrgCrmBoxOpen, setIsOrgCrmBoxOpen] = useState(false);
    const setOrgCrmProjectBoxValue = (value: string | null) => {
        form.setValue("crm_project_id", Number(value) || null);
    }

    useEffect(() => {
        if (selectedCRMSystem === null) {
            form.setValue("crm_id", null);
        }
        if (selectedCRM === null) {
            form.setValue("crm_project_id", null);
        }
    }, [selectedCRMSystem, form, selectedCRM])

    const resetSettingsToDefault = useCallback((overrideParameters?: typeof parameters) => {
        const paramsToUse = overrideParameters ?? parameters;
        form.reset({
            name: projectData?.name || '',
            challenge: projectData?.challenge || '',

            crm_system: projectData?.crm_system || null,
            crm_id: projectData?.crm_id ? String(projectData.crm_id) : null,
            crm_project_id: projectData?.crm_project_id || null,

            selected_configuration: {
                parameter: paramsToUse?.map((parameter) => ({
                    id: parameter.id,
                    value: parameter.value || ''
                })) || [],
            },
        })
    }, [parameters, projectData?.challenge, projectData?.crm_id, projectData?.crm_project_id, projectData?.crm_system, projectData?.name, form])

    // Update parameters when crmProjectData changes (user selects different CRM project)
    useEffect(() => {
        if (fullConfig) {
            const newParameters = fullConfig.withDefaultConfiguration?.parameter?.project || [];
            setParameters(newParameters);
            // Only update the configuration parameters, not the entire form
            form.setValue('selected_configuration', {
                parameter: newParameters.map((parameter) => ({
                    id: parameter.id,
                    value: parameter.value || ''
                }))
            });
        }
    }, [fullConfig, form]);

    // Reset entire form when projectData loads initially
    useEffect(() => {
        if (projectData) {
            resetSettingsToDefault();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectData]);

    const { mutateAsync: editProject, isPending: isPendingEditProject } = useEditProject();
    const { mutateAsync: updateCrmProject, isPending: isPendingUpdateCrmProject } = useUpdateCrmProject();

    const onSubmit = async (data: EditProjectSchemaType) => {
        if (!projectData) {
            throw new Error("Project not found");
        }

        // TODO: Use different notification/toast library. Stacking of messages is needed.
        await editProject({
            id: projectData.id,
            name: data.name,
            challenge: data.challenge,
            crm_project_id: Number(data.crm_project_id) || null,
            crm_id: Number(data.crm_id) || null,
            crm_system: data.crm_system || null,
        })
            .then(() => {
                SuccessPopup("Project updated successfully");
            })
            .catch(() => {
                // TODO: Error message based on the error response
                ErrorPopup("Something went wrong while updating the project.");
            })

        if (data.crm_project_id) {
            await updateCrmProject({
                id: data.crm_project_id || undefined,
                selected_configuration: data.selected_configuration
            })
                .then(() => {
                    SuccessPopup("Project's crm updated successfully");
                })
                .catch(() => {
                    // TODO: Error message based on the error response
                    ErrorPopup("Something went wrong while updating the project's crm.");
                })
        }
    };

    return (
        <>
            {(isPendingEditProject || isPendingUpdateCrmProject) && (<LoaderWithBackdrop />)}
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="flex items-stretch flex-1 gap-4">
                    <div className="flex flex-col flex-1">
                        <div className="flex flex-col flex-1 gap-2">
                            <p className="mb-2 text-xs">Basic configuration</p>
                            <div className="flex flex-col gap-2 px-2">
                                <FormField
                                    control={form.control}
                                    name="name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <Label className="mb-2">Project Name</Label>
                                            <FormControl>
                                                <Input
                                                    placeholder="Enter project name"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="challenge"
                                    render={({ field }) => (
                                        <FormItem>
                                            <Label className="mb-2">Project description</Label>
                                            <FormControl>
                                                <Textarea
                                                    placeholder="Enter project description"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    <Separator orientation="vertical" />

                    <div className="flex flex-col flex-1">
                        {(() => {
                            if (isPendingGetProjectData) return (
                                <div className="grid h-full place-items-center">
                                    <Loader className="animate-spin" />
                                </div>
                            )

                            return (
                                <div className="flex flex-col flex-1 gap-2">
                                    <DynamicHeightScrollArea
                                        offset={80}
                                    >
                                        <p className="mb-2 text-xs">CRM configuration</p>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex flex-col gap-2 px-2 pt-1">
                                                <FormField
                                                    control={form.control}
                                                    name="crm_system"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel className="mb-2">Select CRM System</FormLabel>
                                                            <FormControl>
                                                                <SelectCrmSystemSanitizedCombobox
                                                                    mode='single'
                                                                    selectedValue={field.value || ""}
                                                                    setSelectedValue={field.onChange}
                                                                />
                                                            </FormControl>
                                                        </FormItem>
                                                    )}
                                                />
                                                <FormField
                                                    control={form.control}
                                                    name="crm_id"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel className="mb-2">Select CRM</FormLabel>
                                                            <FormControl>
                                                                {!crmsDataForSelectedSystem?.length ? (
                                                                    <div className="flex flex-col gap-2">
                                                                        <Input
                                                                            placeholder="No CRM available for this system"
                                                                            value="No CRM available for this system"
                                                                            disabled
                                                                        />

                                                                        <Button
                                                                            onClick={() => {
                                                                                addCrmModalSimplified({})
                                                                            }}
                                                                            size='sm'
                                                                            type='button'
                                                                        >
                                                                            Create new CRM
                                                                        </Button>
                                                                    </div>
                                                                ) : (
                                                                    <Select value={field.value || ""} onValueChange={field.onChange}>
                                                                        <div className="flex">
                                                                            <SelectTrigger>
                                                                                {crmsDataForSelectedSystem.find((crm) => String(crm.id) === String(field.value))?.name || "Select Organization CRM"}
                                                                            </SelectTrigger>
                                                                            <Button
                                                                                variant="outline"
                                                                                className="ml-2"
                                                                                onClick={() => field.onChange(null)}
                                                                                disabled={!field.value}
                                                                            >
                                                                                <RotateCcw size={16} />
                                                                            </Button>
                                                                        </div>
                                                                        <SelectContent>
                                                                            {crmsDataForSelectedSystem.map((system) => (
                                                                                <SelectItem key={system.id} value={String(system.id)}>
                                                                                    {system.name}
                                                                                </SelectItem>
                                                                            ))}
                                                                        </SelectContent>
                                                                    </Select>
                                                                )}

                                                            </FormControl>
                                                        </FormItem>
                                                    )}
                                                />
                                                <div className="m-0 mb-3">
                                                    {(crmProjectsData) && (
                                                        <>
                                                            <p className='mb-1 text-sm font-medium'>Select CRM Project (optional)</p>
                                                            <Combobox
                                                                items={[
                                                                    { label: "Clear selection", value: "" },
                                                                    ...(
                                                                        crmProjectsData?.map((crm, i) => ({ label: `${i + 1}. ${crm.name}`, value: `${crm.id}` }))
                                                                        ?? []
                                                                    )
                                                                ]}
                                                                setSelectedValue={setOrgCrmProjectBoxValue}
                                                                selectedValue={orgCrmBoxValue ? String(orgCrmBoxValue) : null}
                                                                setOpen={setIsOrgCrmBoxOpen}
                                                                open={isOrgCrmBoxOpen}
                                                                triggerLabel="Select CRM Project"
                                                            />
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {parameters?.length !== 0 && (
                                                <div className="flex justify-between pe-4">
                                                    <div className="flex items-center">
                                                        <p className="mb-2 text-xs">Parameters</p>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="flex flex-col gap-2 px-2">
                                                <div className="flex flex-col gap-2">
                                                    <div className="">
                                                        {parameters?.map((parameter, i) => (
                                                            <div className=""
                                                                key={parameter.id}
                                                            >
                                                                <FormField
                                                                    control={form.control}
                                                                    name={`selected_configuration.parameter.${i}.value`}
                                                                    render={({ field }) => (
                                                                        <FormItem>
                                                                            <Label className="mb-1">{parameter.name}</Label>
                                                                            <FormControl>
                                                                                <ParameterSelect
                                                                                    parameterData={parameter}
                                                                                    value={field.value}
                                                                                    onChange={field.onChange}
                                                                                    onCreate={(newValue) => {
                                                                                        setParameters((prev) => {
                                                                                            const newParameter = {
                                                                                                ...parameter,
                                                                                                options: [
                                                                                                    ...(parameter.options || []),
                                                                                                    { key: newValue, value: newValue }
                                                                                                ],
                                                                                            };
                                                                                            return [...prev, newParameter];
                                                                                        })
                                                                                    }}
                                                                                />
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {(orgCrmBoxValue && projectData?.crm_id && projectData?.crm_project_id) && (
                                                    <BorderBox variant='info' size='sm'>
                                                        <p className="text-xs">
                                                            <span>If you would like to add additional parameters to this configuration, </span>
                                                            <Link to={`/organization/${orgId}/manage-organization?crmId=${projectData?.crm_id}&secondaryTab=parameters&parametersTab=project`}>
                                                                <span className="font-bold hover:underline">click here.</span>
                                                            </Link>
                                                        </p>
                                                    </BorderBox>
                                                )}

                                                {!orgCrmBoxValue && !!crmsDataForSelectedSystem?.length && (
                                                    <div className="flex flex-col gap-2">
                                                        <div className="text-center">or</div>
                                                        {/* <AddCrmProjectModal
                                                            label="Connect to a new CRM"
                                                        /> */}
                                                        <Button
                                                            onClick={() => {
                                                                addCrmModalSimplified({})
                                                            }}
                                                            size='sm'
                                                            type='button'
                                                        >
                                                            Create new CRM
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </DynamicHeightScrollArea>
                                    <div className="flex justify-end gap-2">
                                        <Button size="sm" variant="secondary" type="button" onClick={resetSettingsToDefault}>Reset</Button>
                                        <Button size="sm" type="submit">Submit</Button>
                                    </div>
                                </div>
                            )
                        })()}

                    </div>
                </form >
            </Form >
        </>
    )
}
